import { RootState } from '@/store';
import { api } from '../core';
import { getMedicalRecords } from '@/api';
import { SORTING_COLUMN_UPLOADED_BY, ORDER_BY_DATE, ORDER_BY_CHECK_DATE, ORDER_BY_DEFAULT } from '@/constants';

export type DocumentDeleteUnassignedResponse = object;

type DocumentDeleteUnassignedProps = {
  abortController?: AbortController;
  documentId: string | null;
  unassignedDocumentId: string | null;
};

const documentDeleteUnassignedApi = api.injectEndpoints({
  endpoints: (builder) => ({
    postDocumentDeleteUnassigned: builder.query<DocumentDeleteUnassignedResponse, DocumentDeleteUnassignedProps>({
      query: ({
        abortController = new AbortController(),
        documentId,
        unassignedDocumentId,
      }: DocumentDeleteUnassignedProps) => ({
        url: `document/delete/unassigned`,
        method: 'POST',
        body: {
          documentId: documentId,
          unassignedDocumentId: unassignedDocumentId,
        },
        signal: abortController.signal,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled, getState }) {
        try {
          await queryFulfilled;
          const state = getState() as RootState;

          if (
            state.medicalRecords.medicalRecordsPagination.pageIndex >= 0 &&
            state.medicalRecords.medicalRecordsPagination.pageSize > 0
          ) {
            // Get sorting params
            const sorting = state.medicalRecords.medicalRecordsSorting;
            const sortingTouched = state.medicalRecords.medicalRecordsSortingTouched;
            let orderBy: string | undefined = ORDER_BY_DEFAULT;
            let orderDirection: string | undefined = 'descending';
            if (sortingTouched && sorting && sorting.length > 0) {
              orderBy = sorting[0].id === SORTING_COLUMN_UPLOADED_BY ? ORDER_BY_DATE : ORDER_BY_CHECK_DATE;
              orderDirection = sorting[0].desc ? 'descending' : 'ascending';
            }

            // Get selected filters from state
            const selectedFilters = state.medicalRecordsFilters.selected;
            const selectedUploadDateRange = state.medicalRecordsFilters.selectedUploadDateRange;
            const nameFilter = state.medicalRecords.medicalRecordsFinding;

            dispatch(
              getMedicalRecords.initiate(
                {
                  pageIndex: state.medicalRecords.medicalRecordsPagination.pageIndex,
                  pageSize: state.medicalRecords.medicalRecordsPagination.pageSize,
                  orderBy,
                  orderDirection,
                  nameFilter: nameFilter.length ? nameFilter : undefined,
                  caseStatus: selectedFilters.caseStatus.length
                    ? selectedFilters.caseStatus.map((fi) => fi.raw)
                    : undefined,
                  checkedBy: selectedFilters.checkedBy.length
                    ? selectedFilters.checkedBy.map((fi) => fi.userId)
                    : undefined,
                  documentStatus: selectedFilters.documentStatus.length
                    ? selectedFilters.documentStatus.map((s) => s.raw)
                    : undefined,
                  providerType: selectedFilters.providerTypes.length
                    ? selectedFilters.providerTypes.map((fi) => fi.raw)
                    : undefined,
                  providerId: selectedFilters.providers.length
                    ? selectedFilters.providers.map((fi) => (fi ? fi.providerId : 'null'))
                    : undefined,
                  uploadedBy: selectedFilters.uploadedBy.length
                    ? selectedFilters.uploadedBy.map((fi) => fi.userId)
                    : undefined,
                  minUploadDate: selectedUploadDateRange.minUploadDate,
                  maxUploadDate: selectedUploadDateRange.maxUploadDate,
                },
                {
                  forceRefetch: true,
                },
              ),
            );
          }
        } catch (error) {
          if (error instanceof Error && error.name === 'AbortError') {
            console.log('Request was aborted');
          } else {
            console.error('Error in postDocumentDeleteUnassigned in onQueryStarted:', error);
          }
        }
      },
      extraOptions: { maxRetries: 0 },
    }),
  }),
});

/**
 * Export hooks for usage in functional components, which are
 * auto-generated based on the defined endpoints
 */
export const { useLazyPostDocumentDeleteUnassignedQuery } = documentDeleteUnassignedApi;
